import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { BreadcrumbNav } from "@/components/BreadcrumbNav";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { publicRoutes } from "@/utils/routes";
import { MapPin, TrendingUp, Users } from "lucide-react";
import { JsonLd } from "@/components/JsonLd";
import { usePageSeo } from "@/hooks/usePageSeo";

export default function LocationsFloridaHubPage() {
  // Idioma y canonical derivados de la URL (ES / EN son páginas distintas).
  const { language, url, onLanguageChange } = usePageSeo();
  const generateCanonicalUrl = () => url;

  const locations = [
    { name: "Orlando", slug: "orlando", population: "2.7M+", zip: "32801" },
    { name: "Miami", slug: "miami", population: "6.1M+", zip: "33101" },
    { name: "Doral", slug: "doral", population: "75K+", zip: "33166" },
    { name: "Tampa", slug: "tampa", population: "3.3M+", zip: "33601" },
    { name: "Kissimmee", slug: "kissimmee", population: "450K+", zip: "34741" },
    { name: "Fort Lauderdale", slug: "fort-lauderdale", population: "1.9M+", zip: "33301" },
    { name: "Hialeah", slug: "hialeah", population: "230K+", zip: "33010" },
    { name: "Pembroke Pines", slug: "pembroke-pines", population: "180K+", zip: "33024" }
  ];

  // Solo enlazamos ciudades con página publicada (evita enlaces internos a 404).
  const publishedLocations = locations.filter((location) =>
    publicRoutes.includes(`/seo-${location.slug}`)
  );

  const content = {
    es: {
      title: "Servicios de Marketing Digital en Florida | SEO y Google Ads Local",
      description: "Agencia de marketing digital especializada en Florida. SEO Local y Google Ads para Orlando, Miami, Tampa y más ciudades. Resultados medibles para negocios hispanos.",
      h1: "Marketing Digital en Florida",
      intro: "Aumenta tu visibilidad y genera más clientes en las principales ciudades de Florida. Estrategias de SEO Local y Google Ads adaptadas al mercado hispano de Orlando, Miami, Tampa y área metropolitana.",
      locationTitle: "Ciudades que Servimos",
      seoLink: "SEO",
      adsLink: "Google Ads",
      ctaTitle: "¿Listo para Crecer en Florida?",
      ctaButton: "Agenda tu reunión inicial gratuita de 30 minutos",
      whyTitle: "Por Qué Elegir Luis ROC en Florida",
      reason1Title: "Experiencia Local",
      reason1Text: "Conocimiento profundo del mercado hispano en Florida y las particularidades de cada ciudad.",
      reason2Title: "Estrategia Bilingüe",
      reason2Text: "Contenido optimizado en español e inglés para alcanzar toda tu audiencia.",
      reason3Title: "Medición orientada a negocio",
      reason3Text: "Tracking completo de leads, llamadas y conversiones con reportes mensuales transparentes."
    },
    en: {
      title: "Digital Marketing Services in Florida | Local SEO & Google Ads",
      description: "Digital marketing agency specialized in Florida. Local SEO and Google Ads for Orlando, Miami, Tampa and more. Measurable results for Hispanic businesses.",
      h1: "Digital Marketing in Florida",
      intro: "Increase your visibility and generate more customers in Florida's main cities. Local SEO and Google Ads strategies adapted to the Hispanic market in Orlando, Miami, Tampa and metropolitan area.",
      locationTitle: "Cities We Serve",
      seoLink: "SEO",
      adsLink: "Google Ads",
      ctaTitle: "Ready to Grow in Florida?",
      ctaButton: "Request Free 30-Minute Initial Consultation",
      whyTitle: "Why Choose Luis ROC in Florida",
      reason1Title: "Local Expertise",
      reason1Text: "Deep knowledge of the Hispanic market in Florida and each city's unique characteristics.",
      reason2Title: "Bilingual Strategy",
      reason2Text: "Content optimized in Spanish and English to reach your entire audience.",
      reason3Title: "Business-focused measurement",
      reason3Text: "Complete tracking of leads, calls and conversions with transparent monthly reports."
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": language === 'es' ? "Servicios" : "Services",
        "item": language === 'es'
          ? "https://luisroc.com/servicios"
          : "https://luisroc.com/en/services"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": language === 'es' ? "Florida" : "Florida Marketing",
        "item": language === 'es'
          ? "https://luisroc.com/servicios-locales-florida"
          : "https://luisroc.com/en/local-services-florida"
      }
    ]
  };


  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Digital Marketing Services in Florida",
    "provider": { "@id": "https://luisroc.com/#organization" },
    "areaServed": [
      { "@type": "City", "name": "Orlando", "addressRegion": "FL" },
      { "@type": "City", "name": "Miami", "addressRegion": "FL" },
      { "@type": "City", "name": "Doral", "addressRegion": "FL" },
      { "@type": "City", "name": "Tampa", "addressRegion": "FL" },
      { "@type": "City", "name": "Kissimmee", "addressRegion": "FL" },
      { "@type": "City", "name": "Fort Lauderdale", "addressRegion": "FL" },
      { "@type": "City", "name": "Hialeah", "addressRegion": "FL" },
      { "@type": "City", "name": "Pembroke Pines", "addressRegion": "FL" }
    ],
    "description": language === 'es'
      ? "SEO Local, Google Ads y Marketing Digital para negocios hispanos en Florida"
      : "Local SEO, Google Ads and digital marketing for Hispanic-owned businesses in Florida"

  };

  return (
    <>
      <Helmet>
        <html lang={language} />
        <title>{content[language].title}</title>
        <meta name="description" content={content[language].description} />
        <link rel="canonical" href={generateCanonicalUrl()} />
        <meta name="geo.region" content="US-FL" />
        <meta name="geo.placename" content="Florida" />
        
        <meta property="og:type" content="website" />
        <meta property="og:url" content={generateCanonicalUrl()} />
        <meta property="og:title" content={content[language].title} />
        <meta property="og:description" content={content[language].description} />
        <meta property="og:locale" content={language === 'es' ? 'es_US' : 'en_US'} />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={content[language].title} />
        <meta name="twitter:description" content={content[language].description} />
      </Helmet>

      <Header language={language} onLanguageChange={onLanguageChange} />
      
      <main className="min-h-screen bg-background">
        <BreadcrumbNav 
          items={language === 'es'
            ? [
                { name: "Servicios", url: "/servicios" },
                { name: "Florida", url: "/servicios-locales-florida" }
              ]
            : [
                { name: "Services", url: "/en/services" },
                { name: "Florida", url: "/en/local-services-florida" }
              ]}
          language={language}
        />


        <section className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
              {content[language].h1}
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
              {content[language].intro}
            </p>
          </div>

          <div className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-8 text-foreground">
              {content[language].locationTitle}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {publishedLocations.map((location) => (
                <Card key={location.slug} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-center gap-2 mb-2">
                      <MapPin className="w-5 h-5 text-primary" />
                      <CardTitle>{location.name}</CardTitle>
                    </div>
                    <CardDescription className="flex items-center gap-4">
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {location.population}
                      </span>
                      <span className="text-xs">ZIP: {location.zip}</span>
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex gap-3">
                      <Button asChild variant="default" className="flex-1">
                        <a href={`/seo-${location.slug}`}>
                          <TrendingUp className="w-4 h-4 mr-2" />
                          {content[language].seoLink}
                        </a>
                      </Button>
                      <Button asChild variant="outline" className="flex-1">
                        <a href={`/google-ads-${location.slug}`}>
                          {content[language].adsLink}
                        </a>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <Card>
              <CardHeader>
                <CardTitle>{content[language].reason1Title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">{content[language].reason1Text}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>{content[language].reason2Title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">{content[language].reason2Text}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>{content[language].reason3Title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">{content[language].reason3Text}</p>
              </CardContent>
            </Card>
          </div>

          <div className="bg-primary/5 rounded-lg p-8 text-center">
            <h2 className="text-3xl font-bold mb-4 text-foreground">
              {content[language].ctaTitle}
            </h2>
            <Button asChild size="lg" className="text-lg">
              <a href="/contacto">
                {content[language].ctaButton}
              </a>
            </Button>
          </div>
        </section>
      </main>

      <Footer language={language} />
      <JsonLd data={breadcrumbSchema} />
      <JsonLd data={serviceSchema} />
    </>
  );
}
